import type { ColumnData, ColumnProp, DataType } from '@revolist/react-datagrid';

import { SelectOption, getEditorOptions, toSelectOption } from './SelectCellEditor';
import { RevoGridBeforeRangeEditEvent } from './types';

export type RevoGridOptionColumns = Record<ColumnProp, readonly SelectOption[]>;

export const REVO_GRID_RANGE_FILL_PROPS = {
  range: true,
  useClipboard: { rangeFill: true },
} as const;

const isClearedValue = (value: unknown) => value === '' || value === null || value === undefined;

const isAllowedValue = (options: readonly SelectOption[], value: unknown): boolean =>
  isClearedValue(value) || options.some((option) => toSelectOption(option).value === value);

export const getOptionColumns = (columns: ColumnData): RevoGridOptionColumns =>
  columns.reduce<RevoGridOptionColumns>((optionColumns, column) => {
    if ('children' in column) {
      return { ...optionColumns, ...getOptionColumns(column.children) };
    }

    const options = getEditorOptions(column.editor);

    return options ? { ...optionColumns, [column.prop]: options } : optionColumns;
  }, {});

/** Drops the props this row is not allowed to write. Returns how many. */
const sanitizeRow = (row: DataType, optionColumns: RevoGridOptionColumns): number =>
  Object.keys(optionColumns).reduce((dropped, prop) => {
    if (!(prop in row) || isAllowedValue(optionColumns[prop], row[prop])) {
      return dropped;
    }
    // eslint-disable-next-line no-param-reassign
    delete row[prop];

    return dropped + 1;
  }, 0);

export const sanitizeRangeEditValues = (
  event: RevoGridBeforeRangeEditEvent,
  optionColumns: RevoGridOptionColumns
): number => {
  const rows = Object.values(event.detail?.data ?? {});

  return rows.reduce((dropped, row) => dropped + sanitizeRow(row, optionColumns), 0);
};
